/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";

import Header from "../Layout/Header";
import Footer from "../Layout/Footer";
import GlobalLoader from "@components/GlobalLoader";
import showErrMessage from "@components/ShowErrMessage";
import { getLogoUrl, handleLogoError } from "@utils/logoHelper";

import { acceptDisclaimer, getCompetition } from "./competition.api";
import {
  PAGE_TYPES,
  errorMessage,
  formatDate,
  pageTypeOfSlug,
} from "./competition.constants";
import {
  hasCustomBackground,
  isFramed,
  themeOf,
  themeStyle,
} from "./competition.theme";
import CornerView from "./CornerView";
import DisclaimerView from "./DisclaimerView";
import PlayArea from "./PlayArea";
import PublicList from "./PublicList";

import "./styles.scss";

/**
 * Menüde gösterilecek sayfalar: admin'in seçtiği sıra; Başla her zaman ilk sırada.
 * Yarışma Köşesi şimdilik menüde yok (ana sayfa için ayrı geliştirme yapılacak); /kose adresi
 * de menüde olmadığı için Başla'ya düşer.
 */
const menuPages = (competition) => {
  const visible = (competition.pages || []).filter(
    (page) => PAGE_TYPES[page.pageType] && page.pageType !== "CORNER"
  );
  if (!visible.some((page) => page.pageType === "START")) {
    visible.unshift({ pageType: "START" });
  }
  return visible;
};

/**
 * Menü. Mobilde tek satırda yatay kaydırılır; sığmadığında sağında / solunda kaydırılabileceğini
 * gösteren oklar çıkar (tıklanınca kaydırır). Seçili sayfa görünür alana getirilir.
 * Yarışma ekranında portal header'ı gizli olduğu için en solda portal logosu durur (ana sayfaya
 * döner); sol üst süsün üstüne binmesin diye sahnenin köşesinde değil, menü satırındadır.
 * Logo zeminsiz olduğundan kaydırma alanının (track) dışında durur; sayfa adları altından geçmez.
 */
const Menu = ({ pages, currentType, onSelect, className = "" }) => {
  const navRef = useRef(null);
  const portalLogo = useSelector((store) => store.auth.loginSettings?.logo);
  const [scroll, setScroll] = useState({ left: false, right: false });

  const update = useCallback(() => {
    const nav = navRef.current;
    if (!nav) return;
    const max = nav.scrollWidth - nav.clientWidth;
    const left = max > 1 && nav.scrollLeft > 1;
    const right = max > 1 && nav.scrollLeft < max - 1;
    setScroll((prev) =>
      prev.left === left && prev.right === right ? prev : { left, right }
    );
  }, []);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;
    update();
    nav.addEventListener("scroll", update, { passive: true });
    const observer = window.ResizeObserver ? new ResizeObserver(update) : null;
    observer?.observe(nav);
    window.addEventListener("resize", update);
    return () => {
      nav.removeEventListener("scroll", update);
      observer?.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [pages.length]);

  // Seçili sayfayı ortala; oklar ilk kez çıktığında içerik kaydığı için o an yeniden ortalanır.
  useEffect(() => {
    const nav = navRef.current;
    const active = nav?.querySelector("button.active");
    if (!active || nav.scrollWidth <= nav.clientWidth) return;
    const navBox = nav.getBoundingClientRect();
    const box = active.getBoundingClientRect();
    nav.scrollLeft +=
      box.left - navBox.left - (nav.clientWidth - box.width) / 2;
    update();
  }, [currentType, scroll.left || scroll.right]);

  const scrollBy = (direction) =>
    navRef.current?.scrollBy({
      left: direction * navRef.current.clientWidth * 0.6,
      behavior: "smooth",
    });

  const scrollable = scroll.left || scroll.right;

  return (
    <nav
      className={`cmp-menu ${scrollable ? "is-scrollable" : ""} ${className}`}
      aria-label="Yarışma menüsü"
    >
      <Link to="/home" className="cmp-home-logo" aria-label="Ana sayfa">
        <img
          src={getLogoUrl(portalLogo)}
          alt=""
          onError={(e) => handleLogoError(e)}
        />
      </Link>
      <div ref={navRef} className="cmp-menu-track">
        {scrollable && (
          <button
            type="button"
            className={`cmp-menu-arrow cmp-menu-arrow-left ${
              scroll.left ? "" : "is-hidden"
            }`}
            aria-label="Menüyü sola kaydır"
            tabIndex={-1}
            onClick={() => scrollBy(-1)}
          >
            <FaChevronLeft />
          </button>
        )}
        {pages.map((page) => (
          <button
            key={page.pageType}
            type="button"
            className={page.pageType === currentType ? "active" : ""}
            style={page.textColor ? { color: page.textColor } : undefined}
            onClick={() => onSelect(page.pageType)}
          >
            {page.title || PAGE_TYPES[page.pageType].title}
          </button>
        ))}
        {scrollable && (
          <button
            type="button"
            className={`cmp-menu-arrow cmp-menu-arrow-right ${
              scroll.right ? "" : "is-hidden"
            }`}
            aria-label="Menüyü sağa kaydır"
            tabIndex={-1}
            onClick={() => scrollBy(1)}
          >
            <FaChevronRight />
          </button>
        )}
      </div>
    </nav>
  );
};

/** Admin'in "Alt görsel 1" (sol üst) ve "Alt görsel 2" (sağ üst) süsleri; web'de ve mobilde. */
const TopDecorations = ({ competition }) => (
  <>
    {competition.bottomImage1Url && (
      <img
        className="cmp-corner-deco cmp-corner-deco-left"
        src={competition.bottomImage1Url}
        alt=""
      />
    )}
    {competition.bottomImage2Url && (
      <img
        className="cmp-corner-deco cmp-corner-deco-right"
        src={competition.bottomImage2Url}
        alt=""
      />
    )}
  </>
);

/**
 * "Alt görsel 3–4" (soru kartının sol/sağ alt figürleri). Figürler yalnızca
 * sahne düzeninde çerçevenin alt köşelerine yerleşir (akışta kartın yanına ayrıca çizilir).
 */
const Decorations = ({ competition, withFigures }) => (
  <>
    <TopDecorations competition={competition} />
    {withFigures && competition.bottomImage3Url && (
      <img
        className="cmp-figure cmp-figure-left"
        src={competition.bottomImage3Url}
        alt=""
      />
    )}
    {withFigures && competition.bottomImage4Url && (
      <img
        className="cmp-figure cmp-figure-right"
        src={competition.bottomImage4Url}
        alt=""
      />
    )}
  </>
);

/** "Alt görsel 3–4" sahne düzeninde soru alanının sol / sağ alt köşesinde (alana göre konumlanır). */
const FrameFigures = ({ competition }) => (
  <>
    {competition.bottomImage3Url && (
      <img
        className="cmp-figure cmp-figure-left"
        src={competition.bottomImage3Url}
        alt=""
      />
    )}
    {competition.bottomImage4Url && (
      <img
        className="cmp-figure cmp-figure-right"
        src={competition.bottomImage4Url}
        alt=""
      />
    )}
  </>
);

const MOBILE_QUERY = "(max-width: 991px)";

/**
 * Dikey arka planın en/boy oranı. Sahne görselin kendi oranıyla çizilsin diye (çarpılmasın)
 * yalnızca mobil/tablet genişliğinde görsel yüklenip ölçülür; web'de indirilmez.
 */
const useMobileArtRatio = (url) => {
  const [ratio, setRatio] = useState(null);
  useEffect(() => {
    setRatio(null);
    if (!url || !window.matchMedia?.(MOBILE_QUERY).matches) return undefined;
    let active = true;
    const image = new Image();
    image.onload = () => {
      if (active && image.naturalWidth && image.naturalHeight) {
        setRatio(`${image.naturalWidth} / ${image.naturalHeight}`);
      }
    };
    image.src = url;
    return () => {
      active = false;
    };
  }, [url]);
  return ratio;
};

const CompetitionPage = () => {
  const { id, page: slug } = useParams();
  const navigate = useNavigate();
  const [competition, setCompetition] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const mobileArtRatio = useMobileArtRatio(
    competition?.backgroundImageMobileUrl
  );

  const load = useCallback(
    () =>
      getCompetition(id)
        .then((res) => {
          setCompetition(res.data);
          setLoadError("");
        })
        .catch((error) =>
          setLoadError(
            error?.status === 404
              ? "Yarışma bulunamadı ya da katılımınıza açık değil."
              : errorMessage(error, "Yarışma yüklenemedi.")
          )
        ),
    [id]
  );

  useEffect(() => {
    setCompetition(null);
    load();
  }, [id]);

  const pageType = pageTypeOfSlug(slug);
  const goTo = (type) => {
    const target = PAGE_TYPES[type]?.slug;
    navigate(target ? `/competitions/${id}/${target}` : `/competitions/${id}`);
  };

  const onAccept = () => {
    if (!agreed) {
      showErrMessage("Devam etmek için şart ve koşulları onaylayınız.");
      return;
    }
    setAccepting(true);
    acceptDisclaimer(id)
      .then(load)
      .catch((error) => showErrMessage(errorMessage(error)))
      .finally(() => setAccepting(false));
  };

  if (!competition) {
    if (!loadError) return <GlobalLoader />;
    return (
      <>
        <Header />
        <div className="container py-5 text-center">
          <p className="mb-4">{loadError}</p>
          <Link to="/competitions" className="btn btn-primary">
            Yarışmalara Dön
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  const theme = themeOf(competition);
  const pages = menuPages(competition);
  const currentType =
    pageType && pages.some((p) => p.pageType === pageType) ? pageType : "START";
  const currentPage = pages.find((p) => p.pageType === currentType);
  const position = (competition.questionPosition || "RIGHT").toLowerCase();
  const framed = isFramed(competition);
  const isCorner = currentType === "CORNER";

  /**
   * Şart onayı ve adres yalnızca kullanıcı bugün yarışmaya başlayabilecekken (OPEN) istenir;
   * "henüz başlamadı", "soru yok", "kapandı" gibi durum mesajları doğrudan gösterilir. Cevap
   * verdikten sonra tekrar girişteki onay ekranı PlayArea'dadır.
   */
  const renderGate = () => {
    const canStartToday = competition.todayState === "OPEN";
    if (
      canStartToday &&
      competition.disclaimer &&
      !competition.disclaimerAccepted
    ) {
      return (
        <DisclaimerView
          text={competition.disclaimer}
          agreed={agreed}
          onAgreedChange={setAgreed}
          busy={accepting}
          onSubmit={onAccept}
        />
      );
    }
    if (canStartToday && competition.addressMissing) {
      return (
        <div className="cmp-card-body cmp-center">
          <p className="cmp-info-text">
            Hediyenizi gönderebilmemiz için yarışmaya katılmadan önce adres
            bilgilerinizi tamamlamanız gerekiyor.
          </p>
          <Link to="/profile/address" className="cmp-btn cmp-btn-primary">
            Adres Ekle
          </Link>
        </div>
      );
    }
    return <PlayArea competition={competition} onDayChanged={load} />;
  };

  /** Başla dışındaki sayfaların içeriği (sahnede çerçevenin içinde, akışta panel olarak). */
  const renderPageContent = () => {
    switch (currentType) {
      case "HOW_TO_PLAY":
      case "RULES":
        return (
          <div className="cmp-panel">
            <h2 className="cmp-panel-title">
              {currentPage?.title || PAGE_TYPES[currentType].title}
            </h2>
            <div className="cmp-rich-text">
              {currentPage?.content || "İçerik yakında eklenecek."}
            </div>
          </div>
        );
      case "GIFTS":
        return <GiftsView competition={competition} />;
      case "WINNERS":
      case "CORRECT_RESPONDERS":
        return <PublicList competition={competition} kind={currentType} />;
      default:
        return null;
    }
  };

  const giftImage =
    competition.giftImageUrl || competition.gifts?.[0]?.giftImageUrl;

  /** Arka plan görseli yokken Başla sayfası: başlık/logo + kart yan yana. */
  const renderFlowStart = () => (
    <div className={`cmp-stage cmp-stage-${position}`}>
      <div className="cmp-hero">
        {competition.logoImageUrl ? (
          <img
            className="cmp-logo"
            src={competition.logoImageUrl}
            alt={competition.name}
          />
        ) : (
          <div className="cmp-title-block">
            <h1 className="cmp-title">
              {competition.competitionTitle || competition.name}
            </h1>
          </div>
        )}
        {competition.competitionSubTitle && (
          <p className="cmp-subtitle">{competition.competitionSubTitle}</p>
        )}
        {(competition.giftName || giftImage) && (
          <div className="cmp-gift-highlight">
            {giftImage && (
              <img src={giftImage} alt={competition.giftName || ""} />
            )}
            <div>
              {competition.dailyGiftCount > 0 && (
                <span className="cmp-gift-count">
                  Her gün {competition.dailyGiftCount} hediye
                </span>
              )}
              {competition.giftName && <strong>{competition.giftName}</strong>}
            </div>
          </div>
        )}
        {competition.finalProgress && (
          <FinalProgress progress={competition.finalProgress} />
        )}
      </div>

      <div className="cmp-card-area">
        {competition.bottomImage3Url && (
          <img
            className="cmp-figure cmp-figure-left"
            src={competition.bottomImage3Url}
            alt=""
          />
        )}
        <div className="cmp-card">{renderGate()}</div>
        {competition.bottomImage4Url && (
          <img
            className="cmp-figure cmp-figure-right"
            src={competition.bottomImage4Url}
            alt=""
          />
        )}
      </div>
    </div>
  );

  /**
   * Sahne düzeni (arka plan görseli var): soru alanı arka planın üstüne sabit boyutta çizilir;
   * web'de kare (konum sol/orta/sağ), mobilde dikey. Admin soru alanı görseli yüklediyse o,
   * yoksa düz beyaz alan. Dikey arka plan yoksa mobilde kart akışta.
   */
  const renderFramed = () => (
    <div
      style={
        mobileArtRatio ? { "--cmp-mobile-ratio": mobileArtRatio } : undefined
      }
      className={[
        "cmp-art",
        `cmp-art-${position}`,
        competition.backgroundImageMobileUrl ? "" : "no-mobile-bg",
        competition.questionAreaImageUrl ? "has-area" : "",
        competition.questionAreaImageMobileUrl ? "has-area-mobile" : "",
      ].join(" ")}
    >
      <TopDecorations competition={competition} />
      <Menu
        className="cmp-art-menu"
        pages={pages}
        currentType={currentType}
        onSelect={goTo}
      />
      {competition.logoImageUrl && (
        <img
          className="cmp-art-logo"
          src={competition.logoImageUrl}
          alt={competition.name}
        />
      )}
      <div className="cmp-frame">
        <FrameFigures competition={competition} />
        <div className="cmp-frame-inner">
          {currentType === "START" ? (
            <>
              {renderGate()}
              {competition.finalProgress && (
                <FinalProgress progress={competition.finalProgress} />
              )}
            </>
          ) : (
            renderPageContent()
          )}
        </div>
      </div>
    </div>
  );

  const renderFlow = () => (
    <>
      <Decorations competition={competition} withFigures={false} />
      <div className="cmp-inner container">
        <Menu pages={pages} currentType={currentType} onSelect={goTo} />
        {currentType === "START" ? renderFlowStart() : renderPageContent()}
        <p className="cmp-dates">
          {formatDate(competition.dateBegin)} –{" "}
          {formatDate(competition.dateEnd)}
        </p>
      </div>
    </>
  );

  let body;
  if (isCorner) {
    body = (
      <div className="cmp-inner cmp-corner-page">
        <CornerView
          competition={competition}
          decorations={<TopDecorations competition={competition} />}
          menu={
            <Menu
              className="cmp-corner-menu"
              pages={pages}
              currentType={currentType}
              onSelect={goTo}
            />
          }
          onGoToCompetition={() => goTo("START")}
        />
      </div>
    );
  } else if (framed) {
    body = renderFramed();
  } else {
    body = renderFlow();
  }

  // Odaklı ekran: portal header / footer gösterilmez; ana sayfaya menüdeki logodan dönülür.
  return (
    <>
      <div
        className={[
          "cmp-page",
          theme.className,
          hasCustomBackground(competition) && !isCorner ? "has-custom-bg" : "",
          framed && !isCorner ? "cmp-framed" : "",
          isCorner ? "cmp-page-corner" : "",
          competition.fontTheme === "HEADING_HEAVY" ? "cmp-font-heavy" : "",
        ].join(" ")}
        style={{
          ...themeStyle(competition),
          // Sayfanın admin'de seçilen yazı rengi: menüdeki adı ve karttaki başlığı
          ...(currentPage?.textColor
            ? { "--cmp-page-title": currentPage.textColor }
            : null),
        }}
      >
        <div className="cmp-bg" aria-hidden="true" />
        {body}
      </div>
    </>
  );
};

const FinalProgress = ({ progress }) => {
  const required = progress.unitsRequired || 0;
  const units = progress.units || 0;
  return (
    <div className="cmp-final-progress cmp-glass">
      <div className="cmp-final-progress-label">
        Finale kalmak için: <strong>{units}</strong> / {required}{" "}
        {progress.unitName || ""}
        {progress.qualified && (
          <span className="cmp-badge">Finale girişe hak kazandınız</span>
        )}
      </div>
      {required > 0 && (
        <div className="cmp-progress">
          <span
            style={{ width: `${Math.min(100, (units / required) * 100)}%` }}
          />
        </div>
      )}
      {progress.qualified && progress.finalStartDate && (
        <p className="cmp-muted">
          Final {formatDate(progress.finalStartDate)} tarihinde başlıyor.
        </p>
      )}
    </div>
  );
};

const GiftsView = ({ competition }) => {
  const gifts = competition.gifts?.length
    ? competition.gifts
    : [
        {
          giftName: competition.giftName,
          giftImageUrl: competition.giftImageUrl,
          dailyCount: competition.dailyGiftCount,
        },
      ];
  return (
    <div className="cmp-panel cmp-gifts">
      <h2 className="cmp-panel-title">Hediyeler</h2>
      <div className={`cmp-gift-grid ${gifts.length === 1 ? "is-single" : ""}`}>
        {gifts.map((gift, index) => (
          <div className="cmp-gift-card" key={index}>
            {gift.giftImageUrl && (
              <img src={gift.giftImageUrl} alt={gift.giftName || ""} />
            )}
            <strong>{gift.giftName}</strong>
            {gift.rankFrom && (
              <span>
                {gift.rankFrom === gift.rankTo
                  ? `${gift.rankFrom}.`
                  : `${gift.rankFrom}.–${gift.rankTo}.`}{" "}
                sıra
              </span>
            )}
            {gift.dailyCount > 0 && <span>Her gün {gift.dailyCount} adet</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CompetitionPage;
